import { useTranslation } from 'react-i18next'

import {
  CaretDownIcon,
  CheckCircleIcon,
  CheckIcon,
  DotsThreeCircleVerticalIcon,
  WarningCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react'
import { useTheme } from 'styled-components'

import { Button, ButtonMenu, IconButton } from '@reveni-io/reveni-ui'

import FilterViewsSkeleton from './FilterViewsSkeleton/FilterViewsSkeleton'
import useEditViewName from './hooks/useEditViewName/useEditViewName'
import ViewVisibilityIndicator from './ViewVisibilityIndicator/ViewVisibilityIndicator'
import { actionSections } from './constants'
import { getActions, getSections, joinCustomViewsAndDefaultViews } from './helpers'
import * as Styles from './styles'

const FilterViews = ({
  loading,
  customViews = [],
  defaultViews = [],
  selectedView,
  onChangeView,
  onDuplicateView,
  onDeleteView,
  hasViewChanges,
  onSaveChanges,
  onCreateView,
  onManageVisibility,
  onDiscardChanges,
  onExportResults,
  showOptions = true,
  onlyDefaultViews,
  onImportInventoryData,
  onImportProductValidation,
  onImportCreateRmas,
  onPageHistory,
  canEditViews = true,
  canManageSelectedView = true,
  canViewDefaultViews = true,
  showVisibilityIndicator = false,
  hasAvailableViews = true,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { viewNameRef, handleEditViewName, isEditingViewName, setIsEditingViewName, onAcceptEditViewName } =
    useEditViewName(onSaveChanges)

  if (loading) {
    return <FilterViewsSkeleton />
  }

  const actions = getActions({
    t,
    selectedView,
    hasViewChanges,
    onDuplicateView,
    onDeleteView,
    onSaveChanges,
    onCreateView,
    onEditViewName: handleEditViewName,
    onManageVisibility,
    onDiscardChanges,
    onExportResults,
    onlyDefaultViews,
    onImportInventoryData,
    onImportProductValidation,
    onImportCreateRmas,
    onPageHistory,
    canEditViews,
    canManageSelectedView,
  })
  const visibleDefaultViews = canViewDefaultViews ? defaultViews : []
  const views = joinCustomViewsAndDefaultViews(customViews, visibleDefaultViews)
  const sections = getSections(views).map(section => ({
    ...section,
    label: t(section.label),
  }))
  const showSelectedViewVisibility = showVisibilityIndicator && Boolean(selectedView)
  const selectedViewName = hasAvailableViews ? t(selectedView?.name) : t('Sin vistas disponibles')
  const iconColor = hasAvailableViews ? theme.colors.text.inverse : theme.colors.disabled.content
  const sectionsWithActions = actionSections.filter(section => actions.some(action => action.section === section.id))
  const translatedActionSections = sectionsWithActions.map(section => ({
    ...section,
    label: t(section.label),
  }))

  return (
    <Styles.Container>
      {isEditingViewName ? (
        <Styles.SelectedViewSelectorText ref={viewNameRef} editing hasViewChanges={hasViewChanges}>
          {selectedView?.name}
        </Styles.SelectedViewSelectorText>
      ) : (
        <ButtonMenu
          sections={sections}
          renderButton={onClick => (
            <Button variant="text" onClick={onClick} disabled={!hasAvailableViews}>
              <Styles.SelectedViewSelectorText
                hasViewChanges={hasViewChanges && !onlyDefaultViews}
                disabled={!hasAvailableViews}
              >
                {showSelectedViewVisibility && <ViewVisibilityIndicator view={selectedView} />}
                {selectedViewName}
                <CaretDownIcon size={24} color={iconColor} />
              </Styles.SelectedViewSelectorText>
            </Button>
          )}
          menuItems={views?.map(view => ({
            ...view,
            label: (
              <Styles.MenuItem key={view.id} isSelected={view.id === selectedView?.id}>
                <CheckIcon size={14} />
                {t(view.name)}
              </Styles.MenuItem>
            ),
            onClick: onChangeView,
          }))}
        />
      )}
      {showOptions && (
        <Styles.ActionsContainer>
          {isEditingViewName && (
            <Styles.EditActionsContainer>
              <p>{t('Escribe un nuevo nombre para la vista')}</p>
              <Styles.EditViewButton success onClick={onAcceptEditViewName}>
                <CheckCircleIcon alt="Accept edit" size={24} />
              </Styles.EditViewButton>
              <Styles.EditViewButton danger onClick={() => setIsEditingViewName(false)}>
                <XCircleIcon alt="Cancel edit" size={24} />
              </Styles.EditViewButton>
            </Styles.EditActionsContainer>
          )}
          {hasViewChanges && !onlyDefaultViews && (
            <Styles.HasChangesText>
              <WarningCircleIcon size={16} />
              {t('Cambios sin guardar')}
            </Styles.HasChangesText>
          )}
          {!isEditingViewName && (
            <ButtonMenu
              groupBySections
              sections={translatedActionSections}
              renderButton={onClick => (
                <IconButton
                  variant="text"
                  onClick={onClick}
                  disabled={!hasAvailableViews}
                  icon={<DotsThreeCircleVerticalIcon data-testid="view actions" size={24} color={iconColor} />}
                />
              )}
              menuItems={actions.map(action => ({
                ...action,
                label: (
                  <Styles.ActionMenuItem>
                    <action.Icon size={16} />
                    {action.label}
                  </Styles.ActionMenuItem>
                ),
              }))}
            />
          )}
        </Styles.ActionsContainer>
      )}
    </Styles.Container>
  )
}

export default FilterViews
